import React, { useDeferredValue, useState } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import {
  type CommandMenuGroupType,
  CommandMenu,
} from '@flipdish/ui-library/components/organisms/CommandMenu';
import { getTranslate } from 'react-localize-redux';

import ErrorBoundary from '../ErrorBoundary';
import { useCommandMenuItems } from './hooks/useCommandMenuItems';

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

const PortalCommandMenuContent = ({ isOpen, onOpenChange }: Props) => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const [query, setQuery] = useState<string>('');
  // Typing stays responsive while the Fuse search over hundreds of items catches up.
  const deferredQuery = useDeferredValue<string>(query);
  const groups = useCommandMenuItems(deferredQuery);

  return (
    <CommandMenu
      items={groups}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      inputValue={query}
      onInputChange={setQuery}
      filter={false}
      placeholder={translate('Find_a_setting_or_a_page') as string}
      labels={{ search: translate('Search') as string }}
      emptyState={
        <p className="px-4 py-6 text-sm text-tertiary">{translate('No_results') as string}</p>
      }
    >
      <CommandMenu.Group>
        <CommandMenu.List>
          {(group: CommandMenuGroupType) => (
            <CommandMenu.Section title={group.title} items={group.items}>
              {(item) => <CommandMenu.Item {...item} />}
            </CommandMenu.Section>
          )}
        </CommandMenu.List>
      </CommandMenu.Group>
    </CommandMenu>
  );
};

/**
 * The portal's global search as a CommandMenu. Mount it only while search is enabled: it runs the
 * search-item hooks (RMS data, reports, dashboards), and a failure in them must not take down the shell.
 */
export const PortalCommandMenu = (props: Props) => (
  <ErrorBoundary identifier="PortalSearch" includeTowlie={false}>
    <PortalCommandMenuContent {...props} />
  </ErrorBoundary>
);

export default PortalCommandMenu;
